import { Contract, providers } from 'ethers';
import { APERTUM_RPC } from './apertumChain';
import {
  DID_ABI,
  DID_NFT_ADDRESS,
  type DidKycState,
  type DidReader,
} from './didEligibility';
import { multicallRead } from './multicall3';

function toNumber(value: { toNumber(): number } | number | bigint): number {
  if (typeof value === 'number') return value;
  if (typeof value === 'bigint') return Number(value);
  return value.toNumber();
}

export function createJsonRpcDidReader(
  rpcUrl: string = APERTUM_RPC
): { reader: DidReader; provider: providers.JsonRpcProvider } {
  const provider = new providers.StaticJsonRpcProvider(rpcUrl, {
    name: 'apertum',
    chainId: 2786,
  });
  return { reader: didReaderFromProvider(provider), provider };
}

export async function readDidKycState(
  didId: number,
  provider: providers.Provider
): Promise<DidKycState> {
  const results = await multicallRead(
    [
      {
        address: DID_NFT_ADDRESS,
        abi: DID_ABI,
        functionName: 'exists',
        args: [didId],
      },
      {
        address: DID_NFT_ADDRESS,
        abi: DID_ABI,
        functionName: 'isKycValid',
        args: [didId],
      },
      {
        address: DID_NFT_ADDRESS,
        abi: DID_ABI,
        functionName: 'getKycExpiry',
        args: [didId],
      },
    ],
    { provider, chunkSize: 3 }
  );
  if (!results[0]?.success || !results[1]?.success || !results[2]?.success) {
    throw new Error('DID KYC multicall failed');
  }
  return {
    exists: Boolean(results[0].data),
    isKycValid: Boolean(results[1].data),
    kycExpiry: toNumber(results[2].data as { toNumber(): number } | number | bigint),
  };
}

export function didReaderFromProvider(
  provider: providers.Provider
): DidReader {
  const did = new Contract(DID_NFT_ADDRESS, DID_ABI, provider);
  return {
    balanceOf: (wallet) => did.balanceOf(wallet),
    getTokenId: (wallet) => did.getTokenId(wallet),
    exists: (didId) => did.exists(didId),
    isKycValid: (didId) => did.isKycValid(didId),
    getKycExpiry: (didId) => did.getKycExpiry(didId),
    readKycState: (didId) => readDidKycState(didId, provider),
  };
}
